import React, { useEffect } from 'react';
import { NextPage } from 'next';
import { UAParser } from 'ua-parser-js';
import MerchantWidgetCalendarPage from '../../../../templates/merchants/widget/calendar/IndexTemplate/MerchantWidgetCalendarPage';
import { init } from '../../../../redux/actions/merchants/services';
import MetaTags from '../../../../components/layouts/MetaTags';
import { FullCalendarViewStyle } from '../../../../domain/entities/BookingEvent';
import { injectAppContextGetServerSideProps } from '../../../_app';
import { MerchantDetail } from '../../../../interfaces/merchants';
import { fetchMerchantDetail } from '../../../../infrastructure/api';
import ErrorTemplate from '../../../../templates/ErrorTemplate';
import { MerchantSite } from '../../../../components/MerchantSite';
import { usePushMerchantDataLayerEffect } from '../../../../hooks/gtm';
import { useAppDispatch } from '../../../../redux';

const FromTypes = {
  DAY: 'agendaDay',
  THREE_DAY: 'agendaThreeDay',
  WEEK: 'agendaWeek',
  MONTH: 'month',
  LIST: 'listMonth',
} as const;
type FromType = typeof FromTypes[keyof typeof FromTypes];
const CalendarViewStyles = new Map([
  [FromTypes.DAY, FullCalendarViewStyle.timeGridDay],
  [FromTypes.THREE_DAY, FullCalendarViewStyle.timeGridThreeDay],
  [FromTypes.WEEK, FullCalendarViewStyle.timeGridWeek],
  [FromTypes.MONTH, FullCalendarViewStyle.dayGridMonth],
  [FromTypes.LIST, FullCalendarViewStyle.listMonth],
]);

interface Query {
  publicId: string;
  from_pc?: FromType;
  from_sp?: FromType;
  servicePublicIds?: string[];
  _ga?: string;
  _gl?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  ga_session_id?: string;
  ga_client_id?: string;
}

interface Props {
  merchantPublicId: string;
  merchantDetail?: MerchantDetail;
  calendarViewStyle: string;
  ga?: string;
  gl?: string;
  servicePublicIds?: string[];
  isUnpublishedSite: boolean;
  isCoubicApp: boolean;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  ga_session_id?: string;
  ga_client_id?: string;
}

const MerchantWidgetCalendar: NextPage<Props> = (props) => {
  const {
    merchantPublicId,
    merchantDetail,
    calendarViewStyle,
    ga,
    gl,
    servicePublicIds,
    isUnpublishedSite,
    isCoubicApp,
    utm_campaign,
    utm_medium,
    utm_source,
    ga_client_id,
    ga_session_id,
  } = props;
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(init(merchantPublicId, merchantDetail?.time_zone ?? 'Asia/Tokyo'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  usePushMerchantDataLayerEffect({ visit_merchant_id: merchantDetail?.id });

  return (
    <MerchantSite
      isUnPublished={isUnpublishedSite}
      isMerchantInfoExist={!!merchantDetail}
      logoImage={merchantDetail?.logo_image || null}
      merchantPublicId={merchantDetail?.public_id || null}
      displayName={merchantDetail?.display_name || null}
      isCoubicApp={isCoubicApp}
    >
      {merchantDetail ? (
        <>
          <style>
            {/* デフォで当たるbodyとFullCalendarのレイアウトをRails(slim)からの移行都合で上書きしている */}
            {`body {
                min-width: 100% !important;
              }

              .fc .fc-daygrid-body-natural .fc-daygrid-day-events {
                margin-bottom: 0px !important;
              }

              .fc .fc-daygrid-event-harness .fc-daygrid-event .fc-event-main-frame {
                padding: 0 !important;
              }

              .fc-col-header-cell-cushion {
                font-size: 12px;
              }

              .fc .fc-toolbar h2 {
                font-size: 100%;
              }

              .fc .fc-toolbar {
                flex-wrap: wrap;
                flex-direction: row;
              }

              @media screen and (max-width:480px) {
                .fc-toolbar-chunk {
                  margin-top: 15px;
                }
              }
          `}
          </style>
          {/* クローラーにindexされたくない */}
          <MetaTags
            noIndex={true}
            googleAnalyticsFourTrackingId={
              merchantDetail.available_google_analytics_four_tracking_id
            }
          />
          <MerchantWidgetCalendarPage
            merchantDetail={merchantDetail}
            calendarViewStyle={calendarViewStyle}
            ga={ga}
            gl={gl}
            servicePublicIds={servicePublicIds}
            utm_campaign={utm_campaign}
            utm_medium={utm_medium}
            utm_source={utm_source}
            ga_client_id={ga_client_id}
            ga_session_id={ga_session_id}
          />
        </>
      ) : (
        <ErrorTemplate statusCode={404} />
      )}
    </MerchantSite>
  );
};

export const getServerSideProps = injectAppContextGetServerSideProps<Props>(
  async (context) => {
    const { req, query, session, token, requestId, isCoubicApp } = context;
    const {
      from_pc,
      from_sp,
      publicId: merchantPublicId,
      _ga,
      _gl,
      servicePublicIds,
      utm_campaign,
      utm_medium,
      utm_source,
      ga_client_id,
      ga_session_id,
    } = query as unknown as Query;
    const uaParser = new UAParser(req?.headers['user-agent']);
    const device = uaParser.getDevice();

    const isSp = device.type !== undefined;
    let calendarViewStyle = from_pc
      ? CalendarViewStyles.get(from_pc)
      : FullCalendarViewStyle.dayGridMonth;
    if (isSp) {
      calendarViewStyle = from_sp
        ? CalendarViewStyles.get(from_sp)
        : FullCalendarViewStyle.timeGridThreeDay;
    }

    const { merchant, isUnpublishedSite } = await fetchMerchantDetail({
      session,
      token,
      requestId,
      merchantPublicId,
    });

    const result = {
      props: {
        merchantPublicId,
        merchantDetail: merchant,
        calendarViewStyle: calendarViewStyle!,
        ga: _ga,
        gl: _gl,
        servicePublicIds,
        isUnpublishedSite,
        isCoubicApp,
        utm_campaign,
        utm_medium,
        utm_source,
        ga_client_id,
        ga_session_id,
      },
    };
    return result;
  },
);

export default MerchantWidgetCalendar;
